import type {
  ActionMotionIntent,
  ActionTransitionSeed,
  ActiveAction,
  ActiveImpact,
  PresentationEvent,
  PresentationFrame,
  SampledEntity,
} from "../core";
import { COURT_WARRIOR_KIT_ID, type CharacterMotionState } from "../renderer/class-kits";
import { STUDIO_LOADOUTS } from "../renderer/humanoid-equipment";
import { ACTION_MOTION_PRESETS } from "../motion";
import type { PresentationSource } from "./types";

const STUDIO_DURATION = 4;
const STUDIO_ACTION_TIMING = Object.freeze({
  telegraph: 0.68,
  commit: 0.24,
  impact: 0.18,
  recover: 0.78,
});
const STUDIO_ACTION_DURATION = Object.values(STUDIO_ACTION_TIMING).reduce((sum, duration) => sum + duration, 0);
export type StudioActionPreset = keyof typeof ACTION_MOTION_PRESETS;
export type StudioActionPhase = keyof typeof STUDIO_ACTION_TIMING;

/**
 * A renderer-owned inspection source. It exercises the exact same frame
 * contract and class kit as live Court, but supplies a single looped pose so
 * animation work can be judged without combat and camera noise.
 */
export class CharacterStudioSource implements PresentationSource {
  readonly id = "studio:court-warrior";
  readonly title = "Character Studio · Court Warrior";
  readonly mode = "studio" as const;
  readonly canSeek = true;
  readonly duration = STUDIO_DURATION;
  readonly events: readonly PresentationEvent[] = [];
  readonly sourceLabel = "Renderer-owned character inspection";

  private currentTime = 0;
  private motion: CharacterMotionState = "idle";
  private rotation = 0;
  private items: readonly string[] = STUDIO_LOADOUTS.full;
  private colors = { body: "#3fa7d6", accent: "#b8f3ff", dark: "#16141a" };
  private actionMotion: ActionMotionIntent = { ...ACTION_MOTION_PRESETS.cleave };
  private transitionFrom: ActionTransitionSeed | undefined;

  get time(): number { return this.currentTime; }
  get actionIntent(): Readonly<ActionMotionIntent> { return this.actionMotion; }

  setMotion(motion: CharacterMotionState): void {
    this.motion = motion;
    this.currentTime = 0;
  }

  setRotation(degrees: number): void {
    this.rotation = degrees * Math.PI / 180;
  }

  setLoadout(loadout: keyof typeof STUDIO_LOADOUTS): void {
    this.items = STUDIO_LOADOUTS[loadout];
  }

  setColor(slot: keyof typeof this.colors, value: string): void {
    if (/^#[0-9a-f]{6}$/i.test(value)) this.colors[slot] = value;
  }

  setActionPolicy(preset: StudioActionPreset): void {
    this.actionMotion = { ...ACTION_MOTION_PRESETS[preset] };
    this.currentTime = 0;
  }

  /** Preview a new policy from the follow-through of another generated action. */
  setTransitionFrom(preset: StudioActionPreset | null): void {
    this.transitionFrom = preset ? {
      motion: { ...ACTION_MOTION_PRESETS[preset] },
      phase: "recover",
      phaseProgress: 0,
    } : undefined;
    this.motion = "action";
    this.currentTime = 0;
  }

  setActionParameter(parameter: "energy" | "reach" | "advance", value: number): void {
    const bounded = Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0;
    this.actionMotion = { ...this.actionMotion, [parameter]: bounded };
  }

  /** Jump to a named action boundary so telegraph/contact/follow poses can be inspected exactly. */
  seekActionPhase(phase: StudioActionPhase, progress = 0): void {
    const boundedProgress = Math.max(0, Math.min(1, Number.isFinite(progress) ? progress : 0));
    const phaseStart = (Object.keys(STUDIO_ACTION_TIMING) as StudioActionPhase[])
      .slice(0, Object.keys(STUDIO_ACTION_TIMING).indexOf(phase))
      .reduce((sum, candidate) => sum + STUDIO_ACTION_TIMING[candidate], 0);
    this.motion = "action";
    this.currentTime = phaseStart + STUDIO_ACTION_TIMING[phase] * boundedProgress;
  }

  reset(): void {
    this.currentTime = 0;
  }

  step(deltaSeconds: number): void {
    this.currentTime = (this.currentTime + Math.max(0, deltaSeconds)) % this.duration;
  }

  seek(time: number): void {
    this.currentTime = Math.max(0, Math.min(this.duration, time));
  }

  sample(): PresentationFrame {
    const progress = this.currentTime / this.duration;
    const action = this.motion === "action" ? studioAction(this.currentTime, this.actionMotion, this.transitionFrom) : null;
    const impacts = this.motion === "hit" ? studioImpact(this.currentTime) : [];
    const entity: SampledEntity = {
      id: "studio-warrior",
      label: "Court Warrior",
      kind: "actor",
      archetype: "shieldbearer",
      presentationKit: COURT_WARRIOR_KIT_ID,
      team: "player",
      position: { x: 0, y: 0, z: 0 },
      rotation: { x: 0, y: this.rotation, z: 0 },
      appearance: {
        items: [...this.items],
        colors: { ...this.colors },
      },
      visible: true,
      movement: this.motion === "locomotion" ? { gait: "run", progress } : null,
      action,
      statuses: this.motion === "defeated" ? ["defeated"] : [],
    };
    return {
      time: this.currentTime,
      duration: this.duration,
      entities: [entity],
      impacts,
      recentEvents: this.motion === "hit" ? studioRecentImpact(this.currentTime) : [],
    };
  }
}

function studioAction(
  time: number,
  motion: ActionMotionIntent,
  transitionFrom?: ActionTransitionSeed,
): ActiveAction {
  const bounded = Math.max(0, Math.min(STUDIO_ACTION_DURATION, time));
  let phase: ActiveAction["phase"] = "recover";
  let phaseProgress = 1;
  let phaseStart = 0;
  for (const candidate of ["telegraph", "commit", "impact", "recover"] as const) {
    const duration = STUDIO_ACTION_TIMING[candidate];
    if (bounded < phaseStart + duration) {
      phase = candidate;
      phaseProgress = (bounded - phaseStart) / duration;
      break;
    }
    phaseStart += duration;
  }
  return {
    eventId: "studio-action",
    action: `procedural ${motion.plane} ${motion.delivery}`,
    phase,
    phaseProgress,
    totalProgress: Math.min(1, bounded / STUDIO_ACTION_DURATION),
    tags: ["melee", ...(motion.delivery === "bash" ? ["shield"] : [])],
    motion: { ...motion },
    transitionFrom: transitionFrom ? {
      motion: { ...transitionFrom.motion },
      phase: transitionFrom.phase,
      phaseProgress: transitionFrom.phaseProgress,
    } : undefined,
  };
}

function studioImpact(time: number): ActiveImpact[] {
  const age = time % 2;
  if (age > 0.42) return [];
  return [{
    eventId: `studio-hit-${Math.floor(time / 2)}`,
    sourceId: "studio-impact-dummy",
    targetId: "studio-warrior",
    outcome: "hit",
    motion: { direction: { x: 0.45, y: 0, z: -0.89 }, magnitude: 0.82, pointHeight: 0.62 },
    age,
  }];
}

function studioRecentImpact(time: number): PresentationEvent[] {
  const age = time % 2;
  if (age > 1.25) return [];
  return [{
    id: `studio-hit-${Math.floor(time / 2)}`,
    type: "impact",
    sourceId: "studio-impact-dummy",
    targetId: "studio-warrior",
    outcome: "hit",
    motion: { direction: { x: 0.45, y: 0, z: -0.89 }, magnitude: 0.82, pointHeight: 0.62 },
    at: time - age,
  }];
}
